import { Cell, Grid, HFlow, isEmpty, Text, VFlow } from 'bold-ui'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import { useListFieldStatus } from 'components/form'
import { Form, FormRenderProps } from 'components/form/core/Form'
import { getFieldError } from 'components/form/core/util'
import { ProcedimentoSelectField, ProcedimentoSelectModel } from 'components/form/field/select/ProcedimentoSelectField'
import { InfoIcon } from 'components/InfoIcon'
import { GrupoProcedimentoEnum, TipoProcedimentoEnum } from 'graphql/types.generated'
import { useProcedimentoAutomatico } from 'hooks/procedimento-automatico/useProcedimentoAutomatico'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { emptyArrayOf } from 'util/array'
import metaPath, { MetaPath } from 'util/metaPath/metaPath'
import { createValidator, maxLength } from 'util/validation'
import { SoapEditableItem } from 'view/atendimentos/atendimento-individual/model'
import { ProcedimentoModel } from 'view/atendimentos/model-procedimento'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'
import { getSexoCidadao } from 'view/atendimentos/utils/util-sexoCidadao'

import { EditableList, useEditableListField } from '../../../components/EditableList'
import { MSG_ERRO_PROCEDIMENTOS_MIN } from '../../finalizacao/constants-finalizacao'
import { getCbosIdsProcedimentos, hasMudancaProcedimentosAutomaticos } from '../utils/util-plano'
import { ProcedimentosPlanoModel } from './IntervencoesProcedimentosView'
import { ProcedimentoPlanoRow } from './ProcedimentoPlanoRow'

export interface ProcedimentoPlanoModel extends ProcedimentoModel, SoapEditableItem {
  observacao?: string
}

const GRUPOS_PROCEDIMENTO = [
  GrupoProcedimentoEnum.ACOES_PROMOCAO_PREVENCAO_SAUDE,
  GrupoProcedimentoEnum.FINALIDADE_DIAGNOSTICA,
  GrupoProcedimentoEnum.CLINICOS,
  GrupoProcedimentoEnum.CIRURGICO,
  GrupoProcedimentoEnum.ACOES_COMPLEMENTARES_ATENCAO_SAUDE,
]

export interface ProcedimentoPlanoProps {
  name: MetaPath<ProcedimentosPlanoModel>
  cidadao?: CidadaoAtendimento
  filtrarProcedimentosOdonto?: boolean
  required: boolean
  controlModifiedStatus: boolean
  dataAtendimento: LocalDate
  onAutomaticChange?(): void
}

const validator = createValidator<ProcedimentoPlanoModel>({
  observacao: [maxLength(200)],
})

const path = metaPath<ProcedimentoPlanoModel>()

export function ProcedimentoPlanoField(props: Readonly<ProcedimentoPlanoProps>) {
  const {
    name,
    cidadao,
    filtrarProcedimentosOdonto,
    required,
    controlModifiedStatus = false,
    dataAtendimento,
    onAutomaticChange,
  } = props
  const { acesso } = useAcessoLotacaoOrEstagio()

  const {
    handleRowChanged,
    handleSubmit,
    meta,
    input: { value },
    resetItemToInitialValue,
  } = useEditableListField({ name: name.procedimentos, isSameRow })

  const { fieldStatus, disabledValues } = useListFieldStatus({
    name: name.procedimentos,
    controlModifiedStatus,
    isSameRow,
  })

  const { removeProcedimentoAutomatico } = useProcedimentoAutomatico({
    nameProcedimentos: name.procedimentos,
    nameCodigosProcedimentosAutomaticosExcluidos: name.codigosProcedimentosAutomaticosExcluidos,
  })

  const listaProcedimentos = useMemo(() => value || emptyArrayOf<ProcedimentoPlanoModel>(), [value])
  const [listaProcedimentosAnterior, setListaProcedimentosAnterior] = useState(listaProcedimentos)

  const hasMudancaProcedAutomaticos = useMemo(
    () => hasMudancaProcedimentosAutomaticos(listaProcedimentos, listaProcedimentosAnterior),
    [listaProcedimentos, listaProcedimentosAnterior]
  )

  useEffect(() => {
    if (hasMudancaProcedAutomaticos) {
      onAutomaticChange()
      setListaProcedimentosAnterior(listaProcedimentos)
    }
  }, [listaProcedimentos, hasMudancaProcedAutomaticos, onAutomaticChange])

  const procedimentoIsEqual = (procedimento: ProcedimentoSelectModel) => {
    return (value || []).some((o2) => procedimento.id === o2.procedimento.id)
  }

  const itemIsDisabled = useCallback(
    (procedimento: ProcedimentoSelectModel) => disabledValues.some((o2) => procedimento.id === o2.procedimento.id),
    [disabledValues]
  )

  const renderForm = (formProps: FormRenderProps<ProcedimentoPlanoModel>) => {
    const handleChange = (newValue: ProcedimentoSelectModel) => {
      newValue && !procedimentoIsEqual(newValue) && formProps.form.submit()
    }

    return (
      <VFlow vSpacing={0.3}>
        <HFlow hSpacing={0.5} alignItems='center'>
          <Text variant='h5'>SIGTAP</Text>
          <InfoIcon icon='infoCircleFilled' text={MSG_ERRO_PROCEDIMENTOS_MIN} />
        </HFlow>
        <ProcedimentoSelectField
          onChange={handleChange}
          name={path.procedimento}
          itemIsEqual={procedimentoIsEqual}
          tipoProcedimento={TipoProcedimentoEnum.CLINICO}
          gruposProcedimento={GRUPOS_PROCEDIMENTO}
          sexo={getSexoCidadao(cidadao)}
          dataNascimento={cidadao?.dataNascimento}
          dataAtendimento={dataAtendimento}
          cboIds={getCbosIdsProcedimentos(acesso)}
          filtrarProcedimentosOdonto={filtrarProcedimentosOdonto}
          loadItemsOnOpen={false}
          error={getFieldError(meta)}
          required={required}
          itemIsDisabled={!isEmpty(disabledValues) ? itemIsDisabled : undefined}
          dataTestId='ProcedimentoPlano'
        />
      </VFlow>
    )
  }

  const showProcedimentosSemStatus = !controlModifiedStatus && !isEmpty(value)
  const showProcedimentosComStatus = controlModifiedStatus && !isEmpty(fieldStatus)

  return (
    <Grid>
      <Cell size={8}>
        <Form<ProcedimentoPlanoModel> render={renderForm} validate={validator} onSubmit={handleSubmit} />
      </Cell>
      {showProcedimentosSemStatus && (
        <Cell size={12}>
          <EditableList>
            {value.map((item) => (
              <ProcedimentoPlanoRow
                key={item._id}
                model={item}
                onChange={handleRowChanged}
                onRemove={removeProcedimentoAutomatico}
              />
            ))}
          </EditableList>
        </Cell>
      )}

      {showProcedimentosComStatus && (
        <Cell size={12}>
          <EditableList>
            {Array.from(fieldStatus, ([item, status]) => (
              <ProcedimentoPlanoRow
                key={item._id}
                model={item}
                onChange={handleRowChanged}
                onRemove={removeProcedimentoAutomatico}
                onReset={resetItemToInitialValue}
                status={status}
                controlModifiedStatus
              />
            ))}
          </EditableList>
        </Cell>
      )}
    </Grid>
  )
}

const isSameRow = (itemA: ProcedimentoPlanoModel, itemB: ProcedimentoPlanoModel) =>
  itemA.procedimento.id === itemB.procedimento.id
